import { Dispatch, SetStateAction, MutableRefObject } from 'react';
import type { NodeUpdateOptions } from '@/features/projects/contexts/ProjectDataContext/types';
import { ProjectNode, ChecklistItem, NodeType } from '@/shared/types/projectData';
import { setNodeAssignees } from '@/shared/utils/projectDataUtils';
import { synchronizeTaskState } from '@/features/projects/services/taskDetailsSync';
import { getNodeId } from '@/features/projects/utils/projectUtils';
import {
  buildCanonicalNodeTypeConversionUpdates,
  buildSafeDocumentNormalizationUpdates,
} from '@/shared/utils/nodeTypeConversionPolicy';
import { getNodeChecklist, supportsNodeChecklist } from './TaskDetailsPanelChecklist';
import { persistPanelFields, persistPanelChecklist, retryPanelPersistence, persistenceState, type PanelPersistenceRef, markPanelIntent, endPanelIntent, panelHasIntent, panelChecklistPending, undoPanelFields } from './TaskDetailsPanelPersistence';
export { persistPanelFields, persistenceState, acknowledgePanelPersistence } from './TaskDetailsPanelPersistence';
export type { PanelPersistenceRef, PanelPersistenceState } from './TaskDetailsPanelPersistence';

/* ============================================================================
 * TYPE DEFINITIONS
 * ============================================================================ */

export interface HandlerDependencies {
  persistenceRef?: PanelPersistenceRef;
  ownerKey?: string;
  ownsSession?: () => boolean;
  isPanelMounted?: () => boolean;
  getEditedTask?: () => Partial<ProjectNode>;
  readSavedTask?: (projectId: string, nodeId: string) => Promise<Partial<ProjectNode>>;
  setSaveError?: (message: string | null) => void;
  // State setters
  setEditedTask: Dispatch<SetStateAction<Partial<ProjectNode>>>;
  setHasChanges: Dispatch<SetStateAction<boolean>>;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setModalHeight: Dispatch<SetStateAction<number | 'auto'>>;
  setModalWidth: Dispatch<SetStateAction<number | 'auto'>>;
  setCurrentChecklist: Dispatch<SetStateAction<ChecklistItem[]>>;
  setLastLocalUpdate: Dispatch<SetStateAction<number>>;
  setTempProgress: Dispatch<SetStateAction<number | null>>;
  setIsDraggingProgress: Dispatch<SetStateAction<boolean>>;
  
  // Context functions
  updateNode: (nodeId: string, updates: Partial<ProjectNode>, options?: NodeUpdateOptions) => Promise<void> | void;
  deleteNode: (nodeId: string) => void;
  createNode: (node: Partial<ProjectNode>) => Promise<void> | void;
  updateProgress: (nodeId: string, progress: number) => Promise<void> | void;
  hasPermission: (resource: string, action: string) => boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
  requireProjectSelectionForCreate?: boolean;
  canCreateTask?: boolean;
  canUpdateTask?: boolean;
  canMoveTask?: boolean;
  projectNodes: ProjectNode[];
  
  // Refs
  modalRef: MutableRefObject<HTMLDivElement | null>;
  
  // Callbacks
  onClose: () => void;
  onBeforeCreate?: (task: Partial<ProjectNode>) => Promise<void> | void;
  
  // State values
  task: Partial<ProjectNode>;
  editedTask: Partial<ProjectNode>;
  isDraggingProgress: boolean;
  tempProgress: number | null;
  hasChanges: boolean;
  lastCommittedTitleRef: MutableRefObject<string>;
  titleDraftStateRef: MutableRefObject<{
    active: boolean;
    hasChangesBeforeDraft: boolean;
  }>;
  pendingTitleAckRef: MutableRefObject<{
    nodeId: string;
    title: string;
    committedAt: number;
  } | null>;
}

const UNTITLED_TITLE_BY_TYPE: Record<NodeType, string> = {
  task: 'Untitled Task',
  group: 'Untitled Group',
  milestone: 'Untitled Milestone',
  document: 'Untitled Document',
};

/**
 * Normalization belongs at a commit boundary. Applying it to an in-progress
 * controlled input makes spaces disappear and replaces an intentionally empty
 * draft before the user has had a chance to type a replacement.
 */
export const normalizeTitleForCommit = (
  title: ProjectNode['title'] | undefined,
  type: ProjectNode['type'] | undefined,
): string => {
  const trimmedTitle = typeof title === 'string' ? title.trim() : '';
  if (trimmedTitle) {
    return trimmedTitle;
  }
  // This is shared persisted project data, so the fallback is deliberately
  // locale-neutral. UI chrome is translated at render time; the canonical
  // node title must not change based on the last editor's language setting.
  return UNTITLED_TITLE_BY_TYPE[type || 'task'];
};

/* ============================================================================
 * BASIC EVENT HANDLERS
 * ============================================================================ */

export const createBasicHandlers = (deps: HandlerDependencies) => {
  const handleClose = () => {
    // Save any pending progress changes before closing
    if (deps.isDraggingProgress && deps.tempProgress !== null && deps.editedTask.id) {
      deps.setLastLocalUpdate(Date.now());
      void persistPanelFields(deps, { progress: deps.tempProgress }, true);
      deps.setTempProgress(null);
      deps.setIsDraggingProgress(false);
    }
    deps.setIsOpen(false);
    setTimeout(deps.onClose, 200);
  };

  const handleUndo = () => {
    const { state, owns, canRender } = persistenceState(deps);
    if (!owns() || !canRender() || (deps.editedTask.id ? !deps.canUpdateTask : !deps.canCreateTask)) return;
    if (state.pending || Object.keys(state.fields).length || panelChecklistPending(state)) {
      deps.setSaveError?.(deps.t('Changes may already be saved. Retry pending changes or reload to confirm saved state.'));
      return;
    }
    // A focused title draft owns the first Undo. Clear its retained intent too,
    // so blur, a context echo, or reopening cannot resurrect the discarded text.
    if (deps.titleDraftStateRef.current.active) {
      createFieldHandlers(deps).handleTitleCancel();
      return;
    }
    if (deps.editedTask.id) return undoPanelFields(deps);
    deps.setEditedTask(deps.task);
    deps.setHasChanges(false);
  };
  
  const handleSave = async () => {
    try {
      if (!deps.canCreateTask) {
        return;
      }
      if (deps.requireProjectSelectionForCreate && !deps.editedTask.projectId) {
        return;
      }
      const taskToCreate = {
        ...deps.editedTask,
        title: normalizeTitleForCommit(deps.editedTask.title, deps.editedTask.type),
      };
      deps.setEditedTask(taskToCreate);
      await deps.onBeforeCreate?.(taskToCreate);
      await deps.createNode(taskToCreate);
      handleClose();
    } catch (error: unknown) {
      alert(error instanceof Error ? error.message : deps.t('Failed to create task'));
    }
  };

  return { handleClose, handleUndo, handleSave };
};

/* ============================================================================
 * FIELD CHANGE HANDLERS
 * ============================================================================ */

export const createFieldHandlers = (deps: HandlerDependencies) => {
  const handleFieldChange = <K extends keyof ProjectNode>(field: K, value: ProjectNode[K]) => {
    if (persistenceState(deps).state.undoRequested) return;
    if (deps.ownsSession?.() === false) return;
    if (deps.editedTask.id && !deps.canUpdateTask) {
      return;
    }

    if (!deps.editedTask.id && !deps.canCreateTask) {
      return;
    }
    
    // Special handling for type changes with animation
    if (field === 'type' && value !== deps.editedTask.type && deps.modalRef.current) {
      const conversion = value === 'document'
        ? buildSafeDocumentNormalizationUpdates(
            deps.editedTask,
            value as ProjectNode['type'],
            deps.projectNodes,
            { [field]: value } as Partial<ProjectNode>,
          )
        : buildCanonicalNodeTypeConversionUpdates(
            deps.editedTask,
            value as ProjectNode['type'],
            deps.projectNodes,
            { [field]: value } as Partial<ProjectNode>,
          );
      if (!conversion.allowed || !conversion.updates) {
        return;
      }

      // Capture current height and width
      const currentHeight = deps.modalRef.current.offsetHeight;
      const currentWidth = deps.modalRef.current.offsetWidth;
      deps.setModalHeight(currentHeight);
      deps.setModalWidth(currentWidth);
      
      // Update the task type
      const newEditedTask = { ...deps.editedTask, ...conversion.updates };
      deps.setEditedTask(newEditedTask);
      deps.setHasChanges(true);
      
      if (newEditedTask.id) {
        void persistPanelFields(deps, conversion.updates);
      }
      
      // After React renders the new content, measure and animate to new height and width
      requestAnimationFrame(() => {
        if (deps.modalRef.current) {
          deps.modalRef.current.style.height = 'auto';
          deps.modalRef.current.style.width = 'auto';
          const newHeight = deps.modalRef.current.offsetHeight;
          const newWidth = deps.modalRef.current.offsetWidth;
          deps.modalRef.current.style.height = `${currentHeight}px`;
          deps.modalRef.current.style.width = `${currentWidth}px`;
          
          requestAnimationFrame(() => {
            if (deps.modalRef.current) {
              deps.modalRef.current.style.height = `${newHeight}px`;
              deps.modalRef.current.style.width = `${newWidth}px`;
              setTimeout(() => {
                if (deps.modalRef.current) {
                  deps.setModalHeight('auto');
                  deps.setModalWidth('auto');
                }
              }, 200);
            }
          });
        }
      });
      return;
    }
    
    // A title is a draft while the header input is focused. Persisting here
    // caused server normalization to race each keystroke (including spaces
    // and an intentionally empty field). Other fields retain autosave.
    if (field === 'title') {
      markPanelIntent(deps, { title: value as string });
      if (!deps.titleDraftStateRef.current.active) {
        deps.titleDraftStateRef.current = {
          active: true,
          hasChangesBeforeDraft: deps.hasChanges,
        };
      }
      deps.setEditedTask(prev => ({ ...prev, title: value as string }));
      deps.setHasChanges(true);
      return;
    }

    // Regular field update
    const isTaskStatus = value === 'todo' || value === 'in-progress' || value === 'done';
    const newEditedTask = field === 'status' && isTaskStatus
      ? {
          ...deps.editedTask,
          ...synchronizeTaskState({ status: value }, 'status', deps.editedTask)
        }
      : { ...deps.editedTask, [field]: value };
    deps.setEditedTask(prev => field === 'status' && isTaskStatus
      ? { ...prev, ...synchronizeTaskState({ status: value }, 'status', prev) }
      : { ...prev, [field]: value });
    deps.setHasChanges(true);
    
    if (newEditedTask.id) {
      // Use atomic operation for progress updates
      if (field === 'progress' && typeof value === 'number') {
        void persistPanelFields(deps, { progress: value }, true);
      } else {
        void persistPanelFields(deps, { [field]: value });
      }
    }
  };

  const handleTitleCommit = () => {
    if (persistenceState(deps).state.undoRequested) return;
    if (deps.ownsSession?.() === false) return;
    const currentTask = deps.getEditedTask?.() || deps.editedTask;
    const title = normalizeTitleForCommit(currentTask.title, currentTask.type);
    const newEditedTask = { ...currentTask, title };
    const previouslyCommittedTitle = deps.lastCommittedTitleRef.current;
    const wasTitleDraft = deps.titleDraftStateRef.current;
    deps.setEditedTask(newEditedTask);
    endPanelIntent(deps, 'title');
    deps.lastCommittedTitleRef.current = title;
    deps.titleDraftStateRef.current = {
      active: false,
      hasChangesBeforeDraft: false,
    };

    if (newEditedTask.id && previouslyCommittedTitle !== title) {
      // A clean title panel should still retain its local acknowledgement
      // until the optimistic/context echo is able to arrive.
      const committedAt = Date.now();
      deps.setLastLocalUpdate(committedAt);
      deps.pendingTitleAckRef.current = {
        nodeId: newEditedTask.id,
        title,
        committedAt,
      };
      void persistPanelFields(deps, { title });
    }

    // End the title input draft without claiming persistence. Retained intent
    // guards Undo; a rejected write restores dirty state and a visible error.
    if (wasTitleDraft.active) {
      deps.setHasChanges(deps.ownerKey ? panelHasIntent(persistenceState(deps).state) : wasTitleDraft.hasChangesBeforeDraft);
    }
  };

  const handleTitleCancel = () => {
    const draftState = deps.titleDraftStateRef.current;
    endPanelIntent(deps, 'title');
    deps.setEditedTask(prev => ({ ...prev, title: deps.lastCommittedTitleRef.current }));
    deps.titleDraftStateRef.current = {
      active: false,
      hasChangesBeforeDraft: false,
    };
    if (draftState.active) {
      deps.setHasChanges(deps.ownerKey ? panelHasIntent(persistenceState(deps).state) : draftState.hasChangesBeforeDraft);
    }
  };

  const handleProjectChange = async (projectId: string) => {
    if (persistenceState(deps).state.undoRequested) return;
    if (!projectId || projectId === deps.editedTask.projectId) {
      return;
    }

    if (!deps.editedTask.id) {
      deps.setEditedTask(prev => ({ ...prev, projectId }));
      deps.setHasChanges(true);
      return;
    }

    if (!deps.canMoveTask) {
      return;
    }

    const {
      children: _children,
      projectName: _projectName,
      projectColor: _projectColor,
      _deleted: _deleted,
      isDeleted: _isDeleted,
      ...baseTask
    } = deps.editedTask as Partial<ProjectNode> & Record<string, unknown>;

    const movedTask: Partial<ProjectNode> = {
      ...baseTask,
      id: getNodeId(deps.editedTask.id),
      projectId,
    };
    const sourceNodeId = deps.editedTask.id;
    const targetNodeId = `${projectId}:${getNodeId(sourceNodeId)}`;

    try {
      await deps.createNode(movedTask);
      try {
        await deps.deleteNode(sourceNodeId);
      } catch (deleteError) {
        try {
          await deps.deleteNode(targetNodeId);
        } catch {
          // Best-effort rollback only; preserve the original delete failure below.
        }
        throw deleteError;
      }
      deps.setEditedTask(movedTask);
      deps.setHasChanges(false);
      deps.setIsOpen(false);
      setTimeout(deps.onClose, 200);
    } catch (error: unknown) {
      alert(error instanceof Error ? error.message : deps.t('Failed to move item to the selected project'));
    }
  };

  const handleBatchFieldChanges = (changes: Partial<ProjectNode>) => {
    if (persistenceState(deps).state.undoRequested) return;
    if (deps.ownsSession?.() === false || (deps.editedTask.id ? !deps.canUpdateTask : !deps.canCreateTask)) return;
    const newEditedTask = { ...(deps.getEditedTask?.() || deps.editedTask), ...changes };
    deps.setEditedTask(prev => ({ ...prev, ...changes }));
    deps.setHasChanges(true);
    
    if (newEditedTask.id) {
      void persistPanelFields(deps, changes);
    }
  };

  const handleTagsChange = (newValue: string) => {
    const sanitizedValue = newValue.replace(/,+/g, ',');
    const newTags = sanitizedValue.split(',').map(t => t.trim()).filter(Boolean);
    handleFieldChange('tags', newTags);
  };

  return {
    handleRetrySave: () => retryPanelPersistence(deps),
    handleFieldChange,
    handleTitleCommit,
    handleTitleCancel,
    handleProjectChange,
    handleBatchFieldChanges,
    handleTagsChange,
  };
};

/* ============================================================================
 * PROGRESS HANDLERS
 * ============================================================================ */

export const createProgressHandlers = (deps: HandlerDependencies) => {
  const synchronizeProgressChecklist = (progress: number) => {
    const synchronized = synchronizeTaskState({ progress }, 'progress', deps.editedTask);
    deps.setCurrentChecklist(synchronized.checklist || getNodeChecklist(deps.editedTask));
  };

  const handleProgressStart = () => {
    if (persistenceState(deps).state.undoRequested) return;
    if (!deps.canUpdateTask) {
      return;
    }
    deps.setIsDraggingProgress(true);
    deps.setTempProgress(deps.editedTask.progress || 0);
  };

  const handleProgressChange = (value: number) => {
    if (persistenceState(deps).state.undoRequested) return;
    if (!deps.canUpdateTask) {
      return;
    }
    if (deps.isDraggingProgress) {
      // Only update visual state during drag
      deps.setTempProgress(value);
      synchronizeProgressChecklist(value);
      deps.setEditedTask(prev => ({
        ...prev,
        ...synchronizeTaskState({ progress: value }, 'progress', prev)
      }));
    } else {
      // Direct input change - update immediately
      deps.setLastLocalUpdate(Date.now());
      synchronizeProgressChecklist(value);
      deps.setEditedTask(prev => ({
        ...prev,
        ...synchronizeTaskState({ progress: value }, 'progress', prev)
      }));
      deps.setHasChanges(true);
      if (deps.editedTask.id) {
        void persistPanelFields(deps, { progress: value }, true);
      }
    }
  };

  const handleProgressEnd = () => {
    if (persistenceState(deps).state.undoRequested) return;
    if (!deps.canUpdateTask) {
      return;
    }
    if (deps.isDraggingProgress && deps.tempProgress !== null && deps.editedTask.id) {
      // Save the final progress immediately on mouse release
      deps.setLastLocalUpdate(Date.now());
      synchronizeProgressChecklist(deps.tempProgress);
      deps.setEditedTask(prev => ({
        ...prev,
        ...synchronizeTaskState({ progress: deps.tempProgress! }, 'progress', prev)
      }));
      void persistPanelFields(deps, { progress: deps.tempProgress }, true);
      deps.setTempProgress(null);
    }
    deps.setIsDraggingProgress(false);
  };

  return { handleProgressStart, handleProgressChange, handleProgressEnd };
};

/* ============================================================================
 * ASSIGNEE & CHECKLIST HANDLERS
 * ============================================================================ */

export const createCollaborationHandlers = (deps: HandlerDependencies) => {
  const handleAssigneesChange = (assignees: string[]) => {
    if (persistenceState(deps).state.undoRequested) return;
    if (deps.ownsSession?.() === false) return;
    if (deps.editedTask.id && !deps.canUpdateTask) {
      return;
    }
    
    const updatedTask = setNodeAssignees(deps.editedTask as ProjectNode, assignees);
    deps.setEditedTask(updatedTask);
    deps.setHasChanges(true);
    
    if (updatedTask.id) {
      void persistPanelFields(deps, {
        assignees: assignees.length > 0 ? assignees : undefined,
        assignee: assignees.length > 0 ? assignees[0] : undefined
      });
    }
  };

  const handleChecklistChange = (updatedChecklist: ChecklistItem[]) => {
    if (persistenceState(deps).state.undoRequested) return;
    if (deps.ownsSession?.() === false) return;
    const nodeType = deps.editedTask.type || deps.task.type;
    if (!supportsNodeChecklist(nodeType)) {
      return;
    }

    if (deps.editedTask.id && !deps.canUpdateTask) {
      return;
    }

    const previousChecklist = getNodeChecklist(deps.editedTask);
    const now = Date.now();
    deps.setLastLocalUpdate(now);
    deps.setCurrentChecklist(updatedChecklist);
    
    // Keep explicit lifecycle state authoritative; checklist edits should not
    // locally coerce an existing task back to todo/0 just because a new item
    // starts unchecked.
    deps.setEditedTask(prevTask => {
      const syncUpdates = nodeType === 'task'
        ? synchronizeTaskState({ checklist: updatedChecklist }, 'checklist', prevTask)
        : { checklist: updatedChecklist };
      
      return { 
        ...prevTask, 
        ...syncUpdates,
        updatedAt: new Date().toISOString() 
      };
    });
    deps.setHasChanges(true);
    
    if (deps.editedTask.id) {
      void persistPanelChecklist(deps, previousChecklist, updatedChecklist);
    }
  };

  return { handleAssigneesChange, handleChecklistChange };
};
